import * as THREE from 'three';
import { Layer } from '../RenderLayers';

/* ═══════════════════════════════════════════════════════════════════════════
   La FUMÉE du canon en surchauffe.

   Purement cosmétique — ne touche jamais la simulation. Un petit nuage de
   bouffées DOUCES qui montent du canon et s'éteignent : le retour visuel de la
   chauffe, plus organique que la seule teinte rouge de la coque. En SURCHAUFFE le
   canon fume un peu ; ENRAYÉ, il crache franchement (émission plus dense, plus
   grosse). L'émission est décidée par `main.render` d'après l'état `heat` du
   joueur ; ici on ne fait que naître, monter, gonfler et s'éteindre.

   Structure-of-Arrays et pool en ANNEAU, comme les éclats d'obstacle : alloué une
   fois, zéro reconstruction, la plus vieille bouffée écrasée au débordement.
   ═══════════════════════════════════════════════════════════════════════════ */

const CAP = 72;

const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _s = new THREE.Vector3();
const _axisZ = new THREE.Vector3(0, 0, 1);

/** Un disque additif à bord TRÈS doux — de la vapeur floue, pas une balle nette. */
function makeSmokeMaterial(): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff, // la teinte (et son extinction) vient de la couleur d'instance
    transparent: true,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  mat.defines = { ...(mat.defines ?? {}), USE_UV: '' };
  mat.onBeforeCompile = (shader) => {
    // Injection AVANT `<specularmap_fragment>` (même piège que les balles : à
    // `<opaque_fragment>` la couleur serait déjà figée).
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <specularmap_fragment>',
      `#include <specularmap_fragment>
{
  vec2 uv = vUv - 0.5;
  float d = length(uv) * 2.0;                 // 0 centre, 1 bord
  float a = 1.0 - smoothstep(0.0, 1.0, d);    // dégradé doux jusqu'au bord
  a *= a;                                      // encore plus mou (vapeur)
  diffuseColor.a *= a * 0.45;                  // globalement translucide
}`,
    );
  };
  mat.customProgramCacheKey = () => 'smoke-v1';
  return mat;
}

export class SmokeFx {
  readonly mesh: THREE.InstancedMesh;

  private readonly x = new Float32Array(CAP);
  private readonly y = new Float32Array(CAP);
  private readonly vx = new Float32Array(CAP);
  private readonly vy = new Float32Array(CAP);
  private readonly life = new Float32Array(CAP);
  private readonly maxLife = new Float32Array(CAP);
  private readonly size0 = new Float32Array(CAP);
  private readonly rot = new Float32Array(CAP);
  private head = 0;
  /** Compteur de dispersion déterministe (pas de RNG) : chaque bouffée part dans
   *  une direction un peu différente, tirée d'une suite fixe. */
  private tick = 0;

  constructor(scene: THREE.Scene) {
    const geo = new THREE.PlaneGeometry(1, 1);
    this.mesh = new THREE.InstancedMesh(geo, makeSmokeMaterial(), CAP);
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(CAP * 3).fill(1),
      3,
    );
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    // Autour du vaisseau, sous ses tirs : la fumée ne doit pas masquer la balle.
    this.mesh.renderOrder = Layer.PlayerBullets - 2;
    scene.add(this.mesh);
  }

  /** Une instance hors champ le temps du warm-up (sinon le programme du matériau
   *  se compilerait à la première bouffée, en pleine surchauffe). */
  primeForWarmup(): void {
    _p.set(100000, 100000, 0);
    _q.identity();
    _s.setScalar(1);
    _m.compose(_p, _q, _s);
    this.mesh.setMatrixAt(0, _m);
    this.mesh.count = 1;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Émet une bouffée depuis (x, y). `intensity` ∈ [0,1] : plus c'est chaud, plus
   *  la bouffée est grosse, rapide et durable (surchauffe → enrayage). */
  emit(x: number, y: number, intensity: number): void {
    const s = this.head % CAP;
    this.head++;
    this.tick++;
    // Dispersion déterministe : deux suites incommensurables pour x et la rotation.
    const jx = ((this.tick * 0.618) % 1) - 0.5;
    const jr = (this.tick * 0.41) % 1;
    this.x[s] = x + jx * 20;
    this.y[s] = y + 6;
    this.vx[s] = jx * 40;
    // Monte (le canon crache vers le haut), d'autant plus vite qu'il est chaud.
    this.vy[s] = 55 + intensity * 55;
    this.maxLife[s] = 0.5 + intensity * 0.35;
    this.life[s] = this.maxLife[s]!;
    this.size0[s] = 9 + intensity * 12;
    this.rot[s] = jr * Math.PI * 2;
  }

  /** Avance et rend les bouffées vivantes. `dt` en secondes réelles. */
  update(dt: number): void {
    const col = this.mesh.instanceColor!;
    let n = 0;
    for (let s = 0; s < CAP; s++) {
      let life = this.life[s]!;
      if (life <= 0) continue;
      if (dt > 0) {
        this.x[s] = this.x[s]! + this.vx[s]! * dt;
        this.y[s] = this.y[s]! + this.vy[s]! * dt;
        // Ralentit en montant (l'air freine la bouffée).
        const damp = 1 - 1.8 * dt > 0 ? 1 - 1.8 * dt : 0;
        this.vx[s] = this.vx[s]! * damp;
        this.vy[s] = this.vy[s]! * damp;
        life -= dt;
        this.life[s] = life > 0 ? life : 0;
        if (this.life[s]! <= 0) continue;
      }
      const k = life / this.maxLife[s]!; // 1 → 0
      // GONFLE en vieillissant (une bouffée qui se dilate), et s'ÉTEINT.
      const size = this.size0[s]! * (1 + (1 - k) * 1.6);
      _p.set(this.x[s]!, this.y[s]!, 0);
      _q.setFromAxisAngle(_axisZ, this.rot[s]!);
      _s.setScalar(size);
      _m.compose(_p, _q, _s);
      this.mesh.setMatrixAt(n, _m);
      // Vapeur gris-bleu froide, qui s'éteint avec la vie (additif → fondu naturel).
      const b = k * 0.5;
      col.array[n * 3] = b * 0.8;
      col.array[n * 3 + 1] = b * 0.85;
      col.array[n * 3 + 2] = b;
      n++;
    }
    this.mesh.count = n;
    if (n > 0) {
      this.mesh.instanceMatrix.clearUpdateRanges();
      this.mesh.instanceMatrix.addUpdateRange(0, n * 16);
      this.mesh.instanceMatrix.needsUpdate = true;
      col.clearUpdateRanges();
      col.addUpdateRange(0, n * 3);
      col.needsUpdate = true;
    }
  }
}
